/**
 * Helpers for project invite requests
 */

import { apolloClient } from '@/apollo';
import {
  AcceptInviteToProjectMutationVariables,
  AcceptInviteToProjectMutation,
  RejectInviteToProjectMutationVariables,
  RejectInviteToProjectMutation,
  UserProjectsDocument,
  UserProjectsQuery,
  UserProjectsQueryVariables,
  UserDocument,
  UserQuery,
  UserQueryVariables,
  AcceptInviteToProjectDocument,
  RejectInviteToProjectDocument,
} from '@/graphql/operations.gen';
import i18n from '@/i18n';
import router from '@/vue/router';
import { NotificationOptions } from '@/store/types';

export const getSuccessNotification = (projectId: string): NotificationOptions => ({
  message: i18n.global.t('team.invitation.accepted'),
  actions: [
    {
      label: i18n.global.t('team.invitation.goto'),
      action: (closeFn) => {
        router.push({ name: 'sources', params: { projectId } });

        closeFn();
      },
    },
  ],
});

export const acceptInviteToProject = (variables: AcceptInviteToProjectMutationVariables) =>
  apolloClient.mutate<AcceptInviteToProjectMutation, AcceptInviteToProjectMutationVariables>({
    mutation: AcceptInviteToProjectDocument,
    variables,
    update(cache, response) {
      cache.evict({ id: `Project:${response.data.acceptInviteToProject.id}` });

      let cacheData = cache.readQuery<UserProjectsQuery, UserProjectsQueryVariables>({
        query: UserProjectsDocument,
      });

      if (!cacheData) {
        cacheData = {
          user: {
            ...cache.readQuery<UserQuery, UserQueryVariables>({
              query: UserDocument,
            }).user,
            projects: [],
            projectsInvited: [],
            projectsJoined: [],
          },
        };
      } else if (
        // project may already be in the list
        cacheData.user.projectsJoined.find(
          (projectJoined) => projectJoined.id === response.data.acceptInviteToProject.id
        )
      ) {
        return;
      }

      cache.writeQuery<UserProjectsQuery, UserProjectsQueryVariables>({
        query: UserProjectsDocument,
        data: {
          user: {
            ...cacheData.user,
            projectsJoined: [response.data.acceptInviteToProject],
          },
        },
      });
    },
  });

export const rejectInviteToProject = (variables: RejectInviteToProjectMutationVariables) =>
  apolloClient.mutate<RejectInviteToProjectMutation, RejectInviteToProjectMutationVariables>({
    mutation: RejectInviteToProjectDocument,
    variables,
    update(cache, response) {
      cache.evict({ id: `Project:${response.data.rejectInviteToProject.id}` });
    },
  });
